import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';
import {
  X,
  Compass,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Clock,
  UserCheck,
  Building2,
  FileCheck,
  PhoneCall,
  Lock,
  Award,
  Barcode,
  Code2,
  AlertCircle,
  HelpCircle,
  ChevronRight,
  Sparkles,
  BookOpen,
  Send,
  Layers,
  ArrowUpRight,
} from 'lucide-react';

interface UibnSubmissionFlowGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultPath?: 'penulis' | 'penerbit_langsung' | 'mandiri' | 'penerbit' | 'ringkasan' | 'regulasi';
}

export const UibnSubmissionFlowGuideModal: React.FC<UibnSubmissionFlowGuideModalProps> = ({
  isOpen,
  onClose,
  defaultPath = 'penulis',
}) => {
  const [activeTab, setActiveTab] = useState<'penulis' | 'penerbit_langsung' | 'ringkasan' | 'regulasi'>('penulis');
  const [penulisSubTab, setPenulisSubTab] = useState<'mandiri' | 'penerbit'>('mandiri');

  useEffect(() => {
    if (defaultPath === 'mandiri') {
      setActiveTab('penulis');
      setPenulisSubTab('mandiri');
    } else if (defaultPath === 'penerbit') {
      setActiveTab('penulis');
      setPenulisSubTab('penerbit');
    } else if (defaultPath === 'penerbit_langsung' || defaultPath === 'penulis' || defaultPath === 'ringkasan' || defaultPath === 'regulasi') {
      setActiveTab(defaultPath);
    }
  }, [defaultPath, isOpen]);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-flow-guide-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
        {/* Modal Header */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-slate-200 bg-gradient-to-r from-indigo-950 via-indigo-900 to-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 text-amber-400 shrink-0">
              <Compass className="w-5 h-5" />
            </div>
            <div>
              <h2 id="modal-flow-guide-title" className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>Alur & Mekanisme Pengajuan UIBN</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 font-bold uppercase tracking-wider">
                  2 Cara Pengajuan
                </span>
              </h2>
              <p className="text-xs text-indigo-200/90 mt-0.5">
                Panduan resmi 2 cara pengajuan nomor unik buku: Melalui <strong>Akun Penulis</strong> (Jalur Mandiri & Jalur Penerbit Terdaftar) atau <strong>Pengajuan Langsung oleh Penerbit</strong> (Akun Penerbit).
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup panduan alur"
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition cursor-pointer shrink-0"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* Top 2-Method Quick Overview Banner */}
        <div className="bg-indigo-50/70 border-b border-indigo-100 px-4 sm:px-6 py-2.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 text-indigo-950 font-semibold">
            <Layers className="w-4 h-4 text-indigo-600 shrink-0" />
            <span>Pilih salah satu dari 2 metode pengajuan sesuai akun dan status kepemilikan Anda:</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="px-2 py-0.5 rounded-md bg-white border border-indigo-200 text-indigo-800 text-[11px] font-bold">
              1. Akun Penulis
            </span>
            <span className="text-slate-400 text-xs">•</span>
            <span className="px-2 py-0.5 rounded-md bg-white border border-purple-200 text-purple-800 text-[11px] font-bold">
              2. Akun Penerbit
            </span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-4 sm:px-6 pt-2.5 gap-2 overflow-x-auto shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('penulis')}
            className={`pb-2.5 px-3 text-xs font-bold border-b-2 flex items-center gap-2 transition cursor-pointer whitespace-nowrap ${
              activeTab === 'penulis'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <UserCheck className="w-4 h-4" />
            <span>1. Pengajuan oleh Penulis (Akun Penulis)</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('penerbit_langsung')}
            className={`pb-2.5 px-3 text-xs font-bold border-b-2 flex items-center gap-2 transition cursor-pointer whitespace-nowrap ${
              activeTab === 'penerbit_langsung'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span>2. Pengajuan Langsung Penerbit (Akun Penerbit)</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('ringkasan')}
            className={`pb-2.5 px-3 text-xs font-bold border-b-2 flex items-center gap-2 transition cursor-pointer whitespace-nowrap ${
              activeTab === 'ringkasan'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <Layers className="w-4 h-4" />
            <span>Perbandingan 2 Cara</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('regulasi')}
            className={`pb-2.5 px-3 text-xs font-bold border-b-2 flex items-center gap-2 transition cursor-pointer whitespace-nowrap ${
              activeTab === 'regulasi'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <Lock className="w-4 h-4" />
            <span>Ketentuan Proteksi & Aset</span>
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-6 flex-1 text-slate-700">
          {/* TAB 1: Pengajuan oleh Penulis */}
          {activeTab === 'penulis' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              {/* Highlight Intro Card */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-indigo-200/80">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shrink-0 mt-0.5 shadow-xs">
                    1
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                      Cara 1: Pengajuan oleh Penulis (Melalui Akun Penulis)
                    </h3>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      Penulis yang mendaftar dan login dengan akun bertipe <strong>Penulis</strong> dapat memilih <strong>2 opsi jalur penerbitan</strong> saat mengisi formulir naskah buku:
                    </p>
                  </div>
                </div>

                {/* Sub-tab Switcher: Mandiri vs Penerbit Terdaftar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4 pt-3 border-t border-indigo-100">
                  <button
                    type="button"
                    onClick={() => setPenulisSubTab('mandiri')}
                    className={`p-3 rounded-xl border text-left transition cursor-pointer flex items-center justify-between ${
                      penulisSubTab === 'mandiri'
                        ? 'bg-white border-blue-600 shadow-xs ring-2 ring-blue-500/20'
                        : 'bg-white/60 border-slate-200 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <UserCheck className="w-4 h-4 text-blue-600" />
                      <div>
                        <div className="font-bold text-xs text-slate-900">Jalur Mandiri (Self-Publishing)</div>
                        <div className="text-[11px] text-slate-500">Langsung ke Admin UIBN</div>
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-blue-100 text-blue-800">
                      Opsi A
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPenulisSubTab('penerbit')}
                    className={`p-3 rounded-xl border text-left transition cursor-pointer flex items-center justify-between ${
                      penulisSubTab === 'penerbit'
                        ? 'bg-white border-purple-600 shadow-xs ring-2 ring-purple-500/20'
                        : 'bg-white/60 border-slate-200 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Building2 className="w-4 h-4 text-purple-600" />
                      <div>
                        <div className="font-bold text-xs text-slate-900">Jalur Penerbit Terdaftar</div>
                        <div className="text-[11px] text-slate-500">Lewat Verifikasi Penerbit Mitra</div>
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-800">
                      Opsi B
                    </span>
                  </button>
                </div>
              </div>

              {/* Sub-tab Content A: Jalur Mandiri */}
              {penulisSubTab === 'mandiri' && (
                <div className="space-y-4 animate-in fade-in duration-150">
                  <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
                    <span className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5 text-blue-700">
                      <UserCheck className="w-4 h-4 text-blue-600" />
                      Tahapan Alur Jalur Mandiri (Penulis Independen):
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold border border-blue-200">
                      Tanpa Lembaga Penerbit
                    </span>
                  </div>

                  <div className="relative pl-6 space-y-4 border-l-2 border-blue-200 ml-3">
                    {/* Step 1 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        1
                      </div>
                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                        <div className="font-bold text-slate-900 text-xs sm:text-sm">
                          Penulis Mengisi Formulir & Memilih &quot;Jalur Mandiri&quot;
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Penulis login ke akun Penulis, klik tombol <strong>&quot;Daftar Buku & Generate UIBN&quot;</strong>, melengkapi metadata buku, dan pada pilihan jalur penerbitan memilih opsi <strong>&quot;Jalur Mandiri&quot;</strong>.
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        2
                      </div>
                      <div className="bg-amber-50/50 p-3.5 rounded-xl border border-amber-200 shadow-2xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-slate-900 text-xs sm:text-sm">
                            Pengajuan Langsung Masuk ke Antrean Admin UIBN
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                            Status: Diajukan
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Naskah langsung terkirim ke Admin Pusat UIBN. Selama dalam status ini, <strong>metadata buku dikunci dari pengeditan</strong> untuk menjaga kepastian data.
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        3
                      </div>
                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                        <div className="font-bold text-slate-900 text-xs sm:text-sm">
                          Verifikasi & Kurasi Admin Pusat UIBN
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Admin Pusat memvalidasi kelayakan judul, kategori, sinopsis, dan orisinalitas naskah (proses peninjauan standar maksimal 1×24 jam kerja).
                        </p>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        4
                      </div>
                      <div className="bg-emerald-50/60 p-3.5 rounded-xl border border-emerald-300 shadow-2xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-emerald-950 text-xs sm:text-sm flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            Persetujuan Resmi & Penerbitan Seluruh Aset UIBN
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
                            Status: Disetujui
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Buku resmi tercatat dengan label <strong>Penerbit Mandiri</strong>. Semua fasilitas otomatis aktif:
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 pt-2 border-t border-emerald-200/80 text-[11px] font-medium text-emerald-900">
                          <div className="flex items-center gap-1 bg-white/80 p-1.5 rounded-lg border border-emerald-200">
                            <Barcode className="w-3.5 h-3.5 text-emerald-700" />
                            <span>Barcode 1D</span>
                          </div>
                          <div className="flex items-center gap-1 bg-white/80 p-1.5 rounded-lg border border-emerald-200">
                            <Code2 className="w-3.5 h-3.5 text-emerald-700" />
                            <span>QR Code 2D</span>
                          </div>
                          <div className="flex items-center gap-1 bg-white/80 p-1.5 rounded-lg border border-emerald-200">
                            <Award className="w-3.5 h-3.5 text-emerald-700" />
                            <span>E-Sertifikat PDF</span>
                          </div>
                          <div className="flex items-center gap-1 bg-white/80 p-1.5 rounded-lg border border-emerald-200">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
                            <span>Widget HTML</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sub-tab Content B: Jalur Penerbit Terdaftar */}
              {penulisSubTab === 'penerbit' && (
                <div className="space-y-4 animate-in fade-in duration-150">
                  <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
                    <span className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5 text-purple-700">
                      <Building2 className="w-4 h-4 text-purple-600" />
                      Tahapan Alur Jalur Penerbit Terdaftar (Kemitraan):
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-purple-50 text-purple-700 font-semibold border border-purple-200">
                      Verifikasi Penerbit Mitra
                    </span>
                  </div>

                  <div className="relative pl-6 space-y-4 border-l-2 border-purple-200 ml-3">
                    {/* Step 1 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        1
                      </div>
                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                        <div className="font-bold text-slate-900 text-xs sm:text-sm">
                          Penulis Memilih Penerbit Mitra & Memasukkan No. WhatsApp Aktif
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Penulis memilih salah satu lembaga penerbit terdaftar dari menu dropdown dan <strong>wajib mengisi nomor WhatsApp aktif</strong> untuk keperluan komunikasi editorial.
                        </p>
                        <div className="mt-2 p-2 rounded-lg bg-purple-50 border border-purple-200 flex items-center gap-2 text-xs text-purple-900">
                          <PhoneCall className="w-3.5 h-3.5 text-purple-700 shrink-0" />
                          <span>No. WhatsApp akan tampil di panel penerbit agar editor dapat langsung berkoordinasi dengan penulis.</span>
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        2
                      </div>
                      <div className="bg-amber-50/50 p-3.5 rounded-xl border border-amber-200 shadow-2xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-slate-900 text-xs sm:text-sm">
                            Pengajuan Masuk ke Dasbor Penerbit Terdaftar
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                            Status: Menunggu Penerbit
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Naskah masuk ke daftar naskah masuk penerbit yang dipilih. Metadata naskah terkunci selama proses kurasi.
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        3
                      </div>
                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs space-y-2">
                        <div className="font-bold text-slate-900 text-xs sm:text-sm">
                          Penerbit Melakukan Verifikasi dengan Checklist Kelayakan
                        </div>
                        <p className="text-xs text-slate-600">
                          Penerbit menekan tombol <strong>&quot;Verifikasi&quot;</strong>, memeriksa 7 butir checklist syarat resmi naskah (judul, orisinalitas sinopsis, daftar isi, tahun & halaman, etika, dan persetujuan penerbit). Setelah semua ceklis terpenuhi, penerbit menyetujui dan meneruskannya ke Admin UIBN.
                        </p>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        4
                      </div>
                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-slate-900 text-xs sm:text-sm">
                            Validasi Final & Persetujuan Admin UIBN Pusat
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200 shrink-0">
                            Status: Diajukan
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Admin UIBN memeriksa kelengkapan akhir dan menerbitkan nomor UIBN berformat kode prefiks resmi penerbit terkait.
                        </p>
                      </div>
                    </div>

                    {/* Step 5 */}
                    <div className="relative group">
                      <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                        5
                      </div>
                      <div className="bg-emerald-50/60 p-3.5 rounded-xl border border-emerald-300 shadow-2xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-emerald-950 text-xs sm:text-sm flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            UIBN Terbit Resmi di Bawah Nama Penerbit Mitra
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
                            Status: Disetujui
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Buku aktif di Katalog Nasional UIBN. Penulis dan Penerbit dapat langsung mengunduh Barcode 1D, QR Code 2D, E-Sertifikat Digital, dan menyematkan Widget HTML.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Pengajuan Langsung oleh Penerbit */}
          {activeTab === 'penerbit_langsung' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              {/* Highlight Intro Card */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-50 via-fuchsia-50 to-indigo-50 border border-purple-200/80">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-purple-600 text-white flex items-center justify-center font-bold text-sm shrink-0 mt-0.5 shadow-xs">
                    2
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                      Cara 2: Pengajuan Langsung oleh Penerbit (Melalui Akun Penerbit)
                    </h3>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      Lembaga Penerbit resmi yang telah terdaftar di UIBN dapat <strong>mendaftarkan naskah/buku produksinya secara langsung ke Admin UIBN</strong> melalui Akun Penerbit tanpa melalui perantara atau tahapan kurasi pihak ketiga.
                    </p>
                  </div>
                </div>

                <div className="mt-3.5 p-3 rounded-xl bg-white/80 border border-purple-200/80 text-xs text-purple-950 flex items-start gap-2.5">
                  <Sparkles className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold block">Keuntungan Pengajuan Langsung oleh Akun Penerbit:</span>
                    <ul className="mt-1 space-y-1 list-disc pl-4 text-slate-700">
                      <li>Nomor UIBN otomatis dipetakan menggunakan <strong>Kode Prefiks Resmi Penerbit</strong> (contoh: 4 digit unik).</li>
                      <li>Pengajuan <strong>langsung diteruskan ke Admin Pusat UIBN</strong> (status: <em>Diajukan</em>) tanpa jeda kurasi perantara.</li>
                      <li>Penerbit dapat mendaftarkan karya penulis internal, staf, maupun naskah resmi lembaga dalam jumlah tak terbatas.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step By Step Flow for Direct Publisher Submission */}
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
                  <span className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5 text-purple-800">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    Tahapan Alur Pengajuan Langsung oleh Akun Penerbit:
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-purple-100 text-purple-800 font-bold border border-purple-200">
                    Penerbit → Admin UIBN
                  </span>
                </div>

                <div className="relative pl-6 space-y-4 border-l-2 border-purple-300 ml-3">
                  {/* Step 1 */}
                  <div className="relative group">
                    <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                      1
                    </div>
                    <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                      <div className="font-bold text-slate-900 text-xs sm:text-sm">
                        Penerbit Masuk ke Akun Penerbit & Membuka Formulir Buku
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Penerbit login dengan kredensial lembaga resmi, menekan tombol <strong>&quot;Daftar Buku & Generate UIBN&quot;</strong>, dan mengisi data naskah (judul, penulis, kategori, sinopsis, dan daftar isi). Nama penerbit otomatis terkunci sesuai profil lembaga penerbit resmi.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative group">
                    <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                      2
                    </div>
                    <div className="bg-amber-50/50 p-3.5 rounded-xl border border-amber-200 shadow-2xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-slate-900 text-xs sm:text-sm">
                          Pengajuan Langsung Dikirim ke Admin Pusat UIBN
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                          Status: Diajukan
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Karena diajukan langsung oleh akun penerbit yang terverifikasi, naskah tidak perlu melalui status <em>&quot;Menunggu Penerbit&quot;</em>, melainkan langsung masuk ke status <strong>&quot;Diajukan&quot;</strong> pada antrean Admin Pusat UIBN.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative group">
                    <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                      3
                    </div>
                    <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                      <div className="font-bold text-slate-900 text-xs sm:text-sm">
                        Verifikasi Administrasi & Validasi Prefiks oleh Admin UIBN
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Admin UIBN mencocokkan kode prefiks penerbit dan validitas struktur naskah sebelum memberikan persetujuan final.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="relative group">
                    <div className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                      4
                    </div>
                    <div className="bg-emerald-50/60 p-3.5 rounded-xl border border-emerald-300 shadow-2xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-emerald-950 text-xs sm:text-sm flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          UIBN Diterbitkan & Masuk Katalog Resmi Penerbit
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
                          Status: Disetujui
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Buku langsung tercatat aktif. Penerbit dapat mengunduh aset cetak massal (Barcode vektor SVG/PNG, QR Code, Sertifikat Digital PDF) serta mengekspor data katalog ke CSV.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Perbandingan & Ringkasan 2 Cara Pengajuan */}
          {activeTab === 'ringkasan' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="p-4 rounded-xl bg-slate-100/80 border border-slate-200 flex items-start gap-3">
                <Layers className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <span className="font-bold text-slate-900 block text-sm mb-1">
                    Matriks Perbandingan 2 Cara Pengajuan UIBN
                  </span>
                  Tabel rangkuman untuk memahami perbedaan pihak pemohon, jenis akun, alur status, dan tahapan verifikasi.
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-2xs">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-800 font-bold">
                      <th className="p-3">Parameter</th>
                      <th className="p-3 bg-blue-50/60 text-blue-950">1A. Penulis (Jalur Mandiri)</th>
                      <th className="p-3 bg-purple-50/60 text-purple-950">1B. Penulis (Jalur Penerbit)</th>
                      <th className="p-3 bg-fuchsia-50/60 text-fuchsia-950">2. Penerbit Langsung</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600">
                    <tr>
                      <td className="p-3 font-semibold text-slate-900">Jenis Akun Pengaju</td>
                      <td className="p-3 bg-blue-50/20">Akun Penulis</td>
                      <td className="p-3 bg-purple-50/20">Akun Penulis</td>
                      <td className="p-3 bg-fuchsia-50/20 font-semibold text-purple-900">Akun Penerbit Resmi</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-slate-900">Nama Penerbit Buku</td>
                      <td className="p-3 bg-blue-50/20">Penerbit Mandiri</td>
                      <td className="p-3 bg-purple-50/20">Penerbit Mitra Pilihan</td>
                      <td className="p-3 bg-fuchsia-50/20">Lembaga Penerbit Sendiri</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-slate-900">Alur Status Naskah</td>
                      <td className="p-3 bg-blue-50/20 font-mono text-[11px]">
                        Draft → <strong className="text-amber-700">Diajukan</strong> → <strong className="text-emerald-700">Disetujui</strong>
                      </td>
                      <td className="p-3 bg-purple-50/20 font-mono text-[11px]">
                        Draft → <strong className="text-purple-700">Menunggu Penerbit</strong> → <strong className="text-amber-700">Diajukan</strong> → <strong className="text-emerald-700">Disetujui</strong>
                      </td>
                      <td className="p-3 bg-fuchsia-50/20 font-mono text-[11px]">
                        Draft → <strong className="text-amber-700">Diajukan</strong> → <strong className="text-emerald-700">Disetujui</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-slate-900">Tahap Kurasi Awal</td>
                      <td className="p-3 bg-blue-50/20">Langsung Admin UIBN</td>
                      <td className="p-3 bg-purple-50/20">Checklist Kurasi Penerbit Mitra</td>
                      <td className="p-3 bg-fuchsia-50/20">Internal Lembaga Penerbit</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-slate-900">Kontak WhatsApp</td>
                      <td className="p-3 bg-blue-50/20">Opsional</td>
                      <td className="p-3 bg-purple-50/20 font-bold text-purple-800">Wajib Diisi</td>
                      <td className="p-3 bg-fuchsia-50/20">Tercatat di Profil Lembaga</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-slate-900">Pengesahan Akhir</td>
                      <td className="p-3 bg-blue-50/20">Admin Pusat UIBN</td>
                      <td className="p-3 bg-purple-50/20">Admin Pusat UIBN</td>
                      <td className="p-3 bg-fuchsia-50/20">Admin Pusat UIBN</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 4: Ketentuan Proteksi & Regulasi Aset */}
          {activeTab === 'regulasi' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 flex items-start gap-3">
                <Lock className="w-5 h-5 text-slate-700 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <span className="font-bold text-slate-900 block text-sm mb-1">
                    Kebijakan Integritas & Proteksi Naskah Selama Pengajuan
                  </span>
                  Untuk menjaga validitas hukum pangkalan data buku nasional, UIBN memberlakukan sistem penguncian metadata dan penahanan aset resmi selama masa kurasi/peninjauan pada seluruh metode pengajuan.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {/* 1. Kunci Metadata */}
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2">
                  <div className="flex items-center gap-2 text-rose-700 font-bold">
                    <Lock className="w-4 h-4" />
                    <span>Metadata Buku Dikunci</span>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    Saat buku berstatus <strong>&quot;Diajukan&quot;</strong> atau <strong>&quot;Menunggu Penerbit&quot;</strong>, judul, pengarang, dan data teknis tidak dapat diedit untuk mencegah manipulasi data selama evaluasi.
                  </p>
                </div>

                {/* 2. E-Sertifikat Digital */}
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700 font-bold">
                    <Award className="w-4 h-4" />
                    <span>E-Sertifikat Digital PDF</span>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    Sertifikat resmi berformat PDF Landscape berstempel kriptografis SHA-256 <strong>hanya diterbitkan setelah disetujui resmi</strong> oleh Admin Pusat UIBN.
                  </p>
                </div>

                {/* 3. Barcode & QR Code */}
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2">
                  <div className="flex items-center gap-2 text-indigo-700 font-bold">
                    <Barcode className="w-4 h-4" />
                    <span>Barcode & QR Code Cetak</span>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    Kode visual cetak (Barcode 1D Code128 dan QR Code 2D resolusi tinggi) berstatus <em>pending</em> dan baru diaktifkan setelah pengesahan status <strong>Disetujui</strong>.
                  </p>
                </div>

                {/* 4. Widget HTML Embed */}
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-700 font-bold">
                    <Code2 className="w-4 h-4" />
                    <span>Widget Verifikasi Lencana</span>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    Lencana terverifikasi untuk website penerbit/toko daring (HTML/JS widget) hanya dapat digenerate setelah buku terverifikasi resmi oleh UIBN.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 sm:px-6 sm:py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500 hidden sm:flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Pendaftaran UIBN Resmi Terbuka untuk Penulis Mandiri & Penerbit Terdaftar</span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-xs transition cursor-pointer ml-auto"
          >
            Mengerti & Tutup Panduan
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
